import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { MoreHorizontal, UserPlus } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { useSession } from '../session-context';
import { apiErrorCode, apiErrorMessage, apiFetch, v1Path } from '../../lib/api';
import type { ListResponse, MemberDto } from '../../lib/work-types';
import { useOverlays } from './overlay-context';
import { BrandEmpty, LoadingRows, PageTitle, WorkPageError } from './page-states';
import { UserAvatar } from '../UserAvatar';
import { memberDisplayName } from '../../lib/work-types';

type ProjectMemberRole = 'admin' | 'member';

type AccessRequestDto = {
  id: string;
  user_id: string;
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
  status: string;
};

function isAccessRequestDto(value: unknown): value is AccessRequestDto {
  return (
    typeof value === 'object'
    && value !== null
    && typeof (value as { id?: unknown }).id === 'string'
    && typeof (value as { user_id?: unknown }).user_id === 'string'
    && typeof (value as { status?: unknown }).status === 'string'
  );
}

function isProjectMemberRole(value: string): value is ProjectMemberRole {
  return value === 'admin' || value === 'member';
}

function isLastAdminError(err: unknown): boolean {
  return apiErrorCode(err) === 'LAST_ADMIN';
}

function RoleBadge({ role }: { role: string }) {
  if (role === 'admin') {
    return <Badge>Admin</Badge>;
  }
  return <Badge variant="secondary">Member</Badge>;
}

export default function ProjectMembersSettings({ projectId }: { projectId: string }) {
  const { email } = useSession();
  const { toast, confirm } = useOverlays();
  const [members, setMembers] = useState<MemberDto[]>([]);
  const [orgMembers, setOrgMembers] = useState<MemberDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [addRole, setAddRole] = useState<ProjectMemberRole>('member');
  const [adding, setAdding] = useState(false);
  const [actingUserId, setActingUserId] = useState<string | null>(null);
  const [requests, setRequests] = useState<AccessRequestDto[]>([]);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  const membersPath = v1Path(`/projects/${projectId}/members`);

  const canManage = useMemo(() => {
    if (!email) return false;
    const self = members.find((m) => m.email === email);
    return self?.role === 'admin' && self.is_active;
  }, [members, email]);

  const availableToAdd = useMemo(() => {
    const activeIds = new Set(members.map((m) => m.user_id));
    return orgMembers.filter((m) => m.is_active && !activeIds.has(m.user_id));
  }, [orgMembers, members]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const projectRes = await apiFetch<{ items: MemberDto[] }>(membersPath);
      setMembers(projectRes.items);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load stream members'));
      setMembers([]);
    } finally {
      setLoading(false);
    }
    try {
      const pending = await apiFetch<{ items: AccessRequestDto[] }>(
        v1Path(`/projects/${projectId}/access-requests`),
      );
      setRequests(pending.items.filter(isAccessRequestDto));
    } catch {
      setRequests([]);
    }
  }, [membersPath, projectId]);

  const loadOrgMembers = useCallback(async () => {
    try {
      const res = await apiFetch<ListResponse<MemberDto>>(v1Path('/members?limit=100&offset=0'));
      setOrgMembers(res.items);
    } catch (err) {
      toast(apiErrorMessage(err, 'Failed to load organization members'));
      setOrgMembers([]);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (addOpen) {
      void loadOrgMembers();
      setSelectedIds([]);
      setAddRole('member');
    }
  }, [addOpen, loadOrgMembers]);

  function toggleSelected(userId: string) {
    setSelectedIds((current) => (
      current.includes(userId)
        ? current.filter((id) => id !== userId)
        : [...current, userId]
    ));
  }

  async function onAdd(event: FormEvent) {
    event.preventDefault();
    if (selectedIds.length === 0) return;
    setAdding(true);
    setError(null);
    try {
      await apiFetch(membersPath, {
        method: 'POST',
        body: JSON.stringify({ user_ids: selectedIds, role: addRole }),
      });
      setAddOpen(false);
      toast(`Added ${selectedIds.length} member${selectedIds.length === 1 ? '' : 's'}`);
      await load();
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to add members');
      setError(message);
      toast(message);
    } finally {
      setAdding(false);
    }
  }

  async function onChangeRole(userId: string, role: ProjectMemberRole) {
    const member = members.find((m) => m.user_id === userId);
    if (!member || member.role === role) return;
    setActingUserId(userId);
    setError(null);
    try {
      await apiFetch(v1Path(`/projects/${projectId}/members/${userId}`), {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      });
      toast(role === 'admin' ? 'Member is now a stream admin' : 'Member role updated');
      await load();
    } catch (err) {
      const message = isLastAdminError(err)
        ? 'Cannot change role: this stream must keep at least one admin.'
        : apiErrorMessage(err, 'Failed to update role');
      setError(message);
      toast(message);
    } finally {
      setActingUserId(null);
    }
  }

  async function onRemove(userId: string) {
    const member = members.find((m) => m.user_id === userId);
    const label = member?.email ?? 'this member';
    const ok = await confirm({
      title: 'Remove from stream',
      description: `Remove ${label} from this stream? They will lose access until added again.`,
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    setActingUserId(userId);
    setError(null);
    try {
      await apiFetch(v1Path(`/projects/${projectId}/members/${userId}`), { method: 'DELETE' });
      toast('Member removed from stream');
      await load();
    } catch (err) {
      const message = isLastAdminError(err)
        ? 'Cannot remove the last stream admin.'
        : apiErrorMessage(err, 'Failed to remove member');
      setError(message);
      toast(message);
    } finally {
      setActingUserId(null);
    }
  }

  async function onApprove(requestId: string): Promise<void> {
    setApprovingId(requestId);
    setError(null);
    try {
      await apiFetch(v1Path(`/projects/${projectId}/access-requests/${requestId}/approve`), {
        method: 'POST',
      });
      toast('Access granted');
      await load();
    } catch (err) {
      const message = apiErrorMessage(err, 'Failed to approve request');
      setError(message);
      toast(message);
    } finally {
      setApprovingId(null);
    }
  }

  return (
    <div className="flex flex-1 flex-col">
      {requests.length > 0 ? (
        <div className="mb-8">
          <PageTitle title="Access requests" count={requests.length} />
          <p className="mb-4 text-sm text-muted-foreground">
            People who asked to join this stream.
          </p>
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-0">Person</TableHead>
                <TableHead className="w-32 text-right"> </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request) => {
                const member: MemberDto = {
                  user_id: request.user_id,
                  role: 'member',
                  is_active: true,
                  email: request.email,
                  first_name: request.first_name ?? undefined,
                  last_name: request.last_name ?? undefined,
                  avatar_url: request.avatar_url,
                };
                return (
                  <TableRow key={request.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <UserAvatar name={memberDisplayName(member)} src={request.avatar_url} size={28} />
                        <span className="text-sm">{request.email ?? request.user_id}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        size="sm"
                        disabled={approvingId === request.id}
                        onClick={() => void onApprove(request.id)}
                      >
                        {approvingId === request.id ? 'Approving…' : 'Approve'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      ) : null}
      <PageTitle
        title="Stream members"
        count={members.length}
        action={
          canManage ? (
            <Button type="button" size="sm" onClick={() => setAddOpen(true)}>
              <UserPlus className="mr-1.5 h-4 w-4" />
              Add members
            </Button>
          ) : null
        }
      />
      <p className="mb-4 text-sm text-muted-foreground">
        People with access to this stream. Stream admins can add organization members and manage roles.
      </p>
      <WorkPageError error={error} projectId={projectId} onJoined={() => void load()} />
      {loading ? (
        <LoadingRows columns={3} />
      ) : members.length === 0 ? (
        <BrandEmpty title="No stream members yet." />
      ) : (
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-0">Email</TableHead>
              <TableHead className="w-32">Role</TableHead>
              {canManage ? <TableHead className="w-20 text-right">Actions</TableHead> : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((member) => (
              <TableRow key={member.user_id}>
                <TableCell className="text-sm">
                  <div className="flex items-center gap-2">
                    <UserAvatar name={memberDisplayName(member)} src={member.avatar_url} size={28} />
                    <span>{member.email ?? member.user_id}</span>
                  </div>
                </TableCell>
                <TableCell>
                  <RoleBadge role={member.role} />
                </TableCell>
                {canManage ? (
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={actingUserId === member.user_id}
                          aria-label={`Actions for ${member.email ?? member.user_id}`}
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {member.role !== 'admin' ? (
                          <DropdownMenuItem
                            onSelect={() => void onChangeRole(member.user_id, 'admin')}
                          >
                            Make stream admin
                          </DropdownMenuItem>
                        ) : null}
                        {member.role !== 'member' ? (
                          <DropdownMenuItem
                            onSelect={() => void onChangeRole(member.user_id, 'member')}
                          >
                            Make member
                          </DropdownMenuItem>
                        ) : null}
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => void onRemove(member.user_id)}
                        >
                          Remove from stream
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                ) : null}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add stream members</DialogTitle>
            <DialogDescription>
              Select organization members to add to this stream.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onAdd} className="space-y-4">
            <div className="grid gap-2">
              <Label>Role</Label>
              <Select
                value={addRole}
                onValueChange={(value) => {
                  if (isProjectMemberRole(value)) setAddRole(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="member">Member</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Organization members</Label>
              {availableToAdd.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No organization members available to add.
                </p>
              ) : (
                <div className="max-h-48 space-y-2 overflow-y-auto rounded-md border p-3">
                  {availableToAdd.map((orgMember) => (
                    <label
                      key={orgMember.user_id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <Checkbox
                        checked={selectedIds.includes(orgMember.user_id)}
                        onCheckedChange={() => toggleSelected(orgMember.user_id)}
                      />
                      <span>{orgMember.email ?? orgMember.user_id}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={adding || selectedIds.length === 0}
              >
                {adding ? 'Adding...' : 'Add members'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
